import type { ConversationListItemDto, ConversationListSortName } from "@multi-chat/shared";
import { previewLabel } from "./inbox-display.js";
import type { ConversationDraftEntry } from "./conversation-draft-store.js";

type Translate = (key: string) => string;

export function visibleDraftText(
  entry: ConversationDraftEntry | undefined,
): string | null {
  if (entry == null) {
    return null;
  }
  const trimmed = entry.text.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function committedDraftText(
  entry: ConversationDraftEntry | undefined,
): string | null {
  if (entry == null || entry.committedAt == null) {
    return null;
  }
  return visibleDraftText(entry);
}

/** Server list activity on the DTO: derived latest-message timestamp. */
export function serverConversationActivityMs(
  item: Pick<ConversationListItemDto, "lastMessageAt">,
): number {
  if (item.lastMessageAt == null) {
    return 0;
  }
  const ms = Date.parse(item.lastMessageAt);
  return Number.isNaN(ms) ? 0 : ms;
}

export function committedDraftActivityMs(
  entry: ConversationDraftEntry | undefined,
): number | null {
  if (committedDraftText(entry) == null || entry?.committedAt == null) {
    return null;
  }
  return entry.committedAt;
}

export function effectiveConversationActivityMs(
  item: Pick<ConversationListItemDto, "lastMessageAt" | "conversationId">,
  drafts: ReadonlyMap<number, ConversationDraftEntry>,
): number {
  const serverMs = serverConversationActivityMs(item);
  const draftMs = committedDraftActivityMs(drafts.get(item.conversationId));
  if (draftMs == null) {
    return serverMs;
  }
  return Math.max(serverMs, draftMs);
}

/** ISO timestamp shown on the row; matches effective activity. */
export function conversationListActivityAt(
  item: ConversationListItemDto,
  drafts: ReadonlyMap<number, ConversationDraftEntry>,
): string | null {
  const serverMs = serverConversationActivityMs(item);
  const draftMs = committedDraftActivityMs(drafts.get(item.conversationId));
  if (draftMs != null && draftMs > serverMs) {
    return new Date(draftMs).toISOString();
  }
  return item.lastMessageAt;
}

export type ConversationListRowPreview =
  | { kind: "draft"; text: string }
  | { kind: "message"; text: string };

export function conversationListRowPreview(
  item: ConversationListItemDto,
  drafts: ReadonlyMap<number, ConversationDraftEntry>,
  t: Translate,
): ConversationListRowPreview {
  const draft = committedDraftText(drafts.get(item.conversationId));
  if (draft != null) {
    return { kind: "draft", text: draft };
  }
  return { kind: "message", text: previewLabel(item.lastMessagePreview, t) };
}

/**
 * Presentation overlay: one chronological timeline over loaded rows.
 * effectiveActivityAt = max(server lastMessageAt, committed draft activity).
 * Does not inject ids missing from `items`. Dedupes by conversationId.
 *
 * Sort directions mirror server contract exactly:
 *   activity_desc → effectiveActivityAt DESC, conversationId DESC
 *   activity_asc  → effectiveActivityAt ASC,  conversationId ASC
 */
export function orderConversationsByEffectiveActivity<
  T extends { conversationId: number; lastMessageAt: string | null },
>(
  items: readonly T[],
  drafts: ReadonlyMap<number, ConversationDraftEntry>,
  sort: ConversationListSortName = "activity_desc",
): T[] {
  const unique: T[] = [];
  const seen = new Set<number>();
  for (const item of items) {
    if (seen.has(item.conversationId)) {
      continue;
    }
    seen.add(item.conversationId);
    unique.push(item);
  }

  const dir = sort === "activity_asc" ? 1 : -1;
  return unique.toSorted((a, b) => {
    const aAt = effectiveConversationActivityMs(a, drafts);
    const bAt = effectiveConversationActivityMs(b, drafts);
    if (aAt !== bAt) {
      return (aAt - bAt) * dir;
    }
    return (a.conversationId - b.conversationId) * dir;
  });
}
